import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { toast } from "sonner";
import {
  Database, RefreshCw, Trash2, Plus, X, ExternalLink,
  CheckCircle2, AlertTriangle, Clock, Rss, Youtube, FileText,
  Globe2, Building2, BookOpen, Radio, Search,
} from "lucide-react";

const KIND_ICON = {
  rss_feed: Rss,
  youtube_channel: Youtube,
  pdf_url: FileText,
  web_article: Globe2,
  gov_sitemap: Building2,
  openalex: BookOpen,
  podcast_rss: Radio,
  manual_upload: FileText,
};

function Badge({ tone = "neutral", children }) {
  const map = {
    neutral: "bg-[#F1EFE9] text-[#5B5B62] border-[#E4E1D8]",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    danger: "bg-red-50 text-red-700 border-red-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    gold: "bg-[#FBF6EB] text-[#7A5B14] border-[#E7D9B3]",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] border rounded-sm ${map[tone] || map.neutral}`}>
      {children}
    </span>
  );
}

export default function KnowledgeSources() {
  const [adapters, setAdapters] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    kind: "rss_feed", label: "", url: "", lang: "fr",
    trust_level: "external", publisher: "", query: "",
    territories: "", professions: "",
  });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get("/knowledge/adapters"),
        api.get("/knowledge/sources"),
      ]);
      setAdapters(a.data.adapters || []);
      setSources(s.data.items || []);
    } catch (e) {
      toast.error("Impossible de charger les sources UKE");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const totalIngested = useMemo(
    () => sources.reduce((acc, s) => acc + (s.total_ingested || 0), 0),
    [sources],
  );

  const doSync = async (sid) => {
    setSyncing((p) => ({ ...p, [sid]: true }));
    try {
      const res = await api.post(`/knowledge/sources/${sid}/sync`);
      const n = res.data.inserted || 0;
      toast.success(res.data.ok ? `${n} ressource(s) ingérée(s)` : `Sync ok · 0 nouvelle ressource`);
      await loadAll();
    } catch (e) {
      toast.error("Sync échouée");
    } finally {
      setSyncing((p) => ({ ...p, [sid]: false }));
    }
  };

  const doDelete = async (sid) => {
    if (!window.confirm("Supprimer cette source ?")) return;
    try {
      await api.delete(`/knowledge/sources/${sid}`);
      toast.success("Source supprimée");
      await loadAll();
    } catch {
      toast.error("Suppression échouée");
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    const payload = {
      kind: form.kind,
      label: form.label.trim(),
      url: form.url.trim() || null,
      lang: form.lang,
      trust_level: form.trust_level,
      publisher: form.publisher.trim() || null,
      query: form.kind === "openalex" ? (form.query.trim() || null) : null,
      territories: form.territories.split(",").map((s) => s.trim()).filter(Boolean),
      professions: form.professions.split(",").map((s) => s.trim()).filter(Boolean),
      active: true,
    };
    if (!payload.label) { toast.error("Label requis"); return; }
    if (form.kind !== "openalex" && !payload.url) { toast.error("URL requise"); return; }
    if (form.kind === "openalex" && !payload.query) { toast.error("Query requise pour OpenAlex"); return; }
    try {
      await api.post("/knowledge/sources", payload);
      toast.success("Source ajoutée");
      setAddOpen(false);
      setForm({ ...form, label: "", url: "", publisher: "", query: "", territories: "", professions: "" });
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ajout échoué");
    }
  };

  const runSearch = async () => {
    if (!searchQ.trim()) return;
    try {
      const res = await api.post("/knowledge/search", { q: searchQ.trim(), limit: 10 });
      setSearchResults(res.data.items || []);
    } catch {
      toast.error("Recherche échouée");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="knowledge-sources-page">
      <TopBar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] mb-1">UKE™</div>
            <h1 className="text-3xl font-display text-[#0B2545] tracking-tight">Universal Knowledge Engine</h1>
            <p className="mt-2 text-sm text-[#5B5B62] max-w-2xl">
              Le moteur de connaissance central d&apos;ALTIORA. Chaque source ingérée est
              auto-indexée par IA (résumé, tags, dimensions, professions, territoires)
              et devient immédiatement exploitable par Discovery, Radar, HOE, Copilot et Studio.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="uke-search-btn"
              onClick={() => setSearchOpen(true)}
              className="inline-flex items-center gap-2 border border-[#E4E1D8] px-3 py-2 text-sm text-[#1F2937] hover:bg-white rounded-md"
            >
              <Search className="h-4 w-4" strokeWidth={1.5} /> Rechercher
            </button>
            <button
              data-testid="uke-add-source-btn"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-2 bg-[#0B2545] hover:bg-[#123163] text-white px-3 py-2 text-sm rounded-md"
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} /> Nouvelle source
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Stat label="Sources actives" value={sources.filter((s) => s.active).length} icon={Database} testId="uke-stat-sources" />
          <Stat label="Adaptateurs" value={adapters.length} icon={Rss} testId="uke-stat-adapters" />
          <Stat label="Total ingéré" value={totalIngested} icon={CheckCircle2} tone="gold" testId="uke-stat-ingested" />
          <Stat label="En attente clé" value={adapters.filter((a) => !a.is_live).length} icon={AlertTriangle} tone="warn" testId="uke-stat-mock" />
        </div>

        {/* Adapters */}
        <div className="mb-8">
          <h2 className="text-sm font-mono uppercase tracking-[0.18em] text-[#5B5B62] mb-3">Adaptateurs disponibles</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {adapters.map((a) => {
              const Icon = KIND_ICON[a.kind] || Database;
              return (
                <div key={a.kind} className="border border-[#E4E1D8] bg-white rounded-md p-3" data-testid={`uke-adapter-${a.kind}`}>
                  <div className="flex items-center justify-between">
                    <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
                    <Badge tone={a.is_live ? "success" : "warn"}>{a.is_live ? "LIVE" : "MOCK"}</Badge>
                  </div>
                  <div className="mt-2 text-sm text-[#1F2937]">{a.label_fr}</div>
                  <div className="mt-1 text-[10px] font-mono text-[#8B8B8F]">{a.kind}</div>
                  {a.needs_env.length > 0 && (
                    <div className="mt-1 text-[10px] font-mono text-[#8B8B8F]">env: {a.needs_env.join(", ")}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sources */}
        <div>
          <h2 className="text-sm font-mono uppercase tracking-[0.18em] text-[#5B5B62] mb-3">Sources enregistrées ({sources.length})</h2>
          {loading ? (
            <div className="text-sm text-[#5B5B62]">Chargement…</div>
          ) : sources.length === 0 ? (
            <div className="border border-dashed border-[#E4E1D8] rounded-md p-8 text-center text-sm text-[#5B5B62]">
              Aucune source. Cliquez sur « Nouvelle source ».
            </div>
          ) : (
            <div className="space-y-3">
              {sources.map((s) => {
                const Icon = KIND_ICON[s.kind] || Database;
                const status = s.last_sync_status;
                return (
                  <div key={s.id} className="border border-[#E4E1D8] bg-white rounded-md p-4 flex items-start gap-3" data-testid={`uke-source-${s.id}`}>
                    <div className="mt-1"><Icon className="h-5 w-5 text-[#0B2545]" strokeWidth={1.5} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-medium text-[#1F2937] truncate">{s.label}</div>
                        <Badge tone="neutral">{s.kind}</Badge>
                        <Badge tone={s.trust_level === "verified" ? "success" : s.trust_level === "partner" ? "gold" : "neutral"}>{s.trust_level}</Badge>
                        {s.total_ingested > 0 && <Badge tone="gold">{s.total_ingested} ingérées</Badge>}
                      </div>
                      {s.url && (
                        <a href={s.url} target="_blank" rel="noreferrer" className="mt-1 text-xs text-[#0B2545] hover:underline inline-flex items-center gap-1 truncate max-w-full">
                          <ExternalLink className="h-3 w-3" /> {s.url}
                        </a>
                      )}
                      {s.query && <div className="mt-1 text-xs text-[#5B5B62] font-mono">query: {s.query}</div>}
                      <div className="mt-2 flex items-center gap-3 text-[11px] text-[#8B8B8F] font-mono">
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {s.last_sync_at ? new Date(s.last_sync_at).toLocaleString() : "jamais synchronisée"}</span>
                        {status && status.startsWith("error") && <span className="text-red-600">· {status.slice(0, 60)}</span>}
                        {status === "ok" && <span className="text-emerald-600">· ok (+{s.last_sync_count || 0})</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`uke-sync-${s.id}`}
                        onClick={() => doSync(s.id)}
                        disabled={syncing[s.id]}
                        className="inline-flex items-center gap-1 border border-[#E4E1D8] px-3 py-1.5 text-xs text-[#0B2545] hover:bg-[#F1EFE9] rounded-md disabled:opacity-50"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${syncing[s.id] ? "animate-spin" : ""}`} strokeWidth={1.5} />
                        {syncing[s.id] ? "Sync…" : "Sync"}
                      </button>
                      <button
                        data-testid={`uke-del-${s.id}`}
                        onClick={() => doDelete(s.id)}
                        className="inline-flex items-center border border-[#E4E1D8] p-1.5 text-[#5B5B62] hover:text-red-600 hover:border-red-200 rounded-md"
                        title="Supprimer"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add source modal */}
      {addOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setAddOpen(false)}>
          <div className="bg-white rounded-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="uke-add-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-display text-[#0B2545]">Nouvelle source</h3>
              <button onClick={() => setAddOpen(false)} className="text-[#5B5B62] hover:text-[#1F2937]"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submitAdd} className="space-y-3">
              <Field label="Adaptateur">
                <select data-testid="uke-form-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm">
                  {adapters.map((a) => <option key={a.kind} value={a.kind}>{a.label_fr} ({a.kind})</option>)}
                </select>
              </Field>
              <Field label="Libellé">
                <input data-testid="uke-form-label" required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" placeholder="Ex: Radio-Canada · Économie" />
              </Field>
              {form.kind !== "openalex" && (
                <Field label="URL">
                  <input data-testid="uke-form-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" placeholder="https://…" />
                </Field>
              )}
              {form.kind === "openalex" && (
                <Field label="Query OpenAlex">
                  <input data-testid="uke-form-query" value={form.query} onChange={(e) => setForm({ ...form, query: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" placeholder="ex: cybersecurity workforce" />
                </Field>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Langue">
                  <select data-testid="uke-form-lang" value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm">
                    <option value="fr">fr</option><option value="en">en</option><option value="es">es</option><option value="ar">ar</option>
                  </select>
                </Field>
                <Field label="Confiance">
                  <select data-testid="uke-form-trust" value={form.trust_level} onChange={(e) => setForm({ ...form, trust_level: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm">
                    <option value="verified">verified</option><option value="partner">partner</option><option value="community">community</option><option value="external">external</option>
                  </select>
                </Field>
              </div>
              <Field label="Éditeur (optionnel)">
                <input value={form.publisher} onChange={(e) => setForm({ ...form, publisher: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" />
              </Field>
              <Field label="Territoires (séparés par virgule)">
                <input value={form.territories} onChange={(e) => setForm({ ...form, territories: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" placeholder="quebec, montreal" />
              </Field>
              <Field label="Professions (séparées par virgule)">
                <input value={form.professions} onChange={(e) => setForm({ ...form, professions: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" placeholder="analyste_soc, developpeur" />
              </Field>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setAddOpen(false)} className="border border-[#E4E1D8] px-4 py-2 text-sm rounded-md">Annuler</button>
                <button data-testid="uke-form-submit" type="submit" className="bg-[#0B2545] hover:bg-[#123163] text-white px-4 py-2 text-sm rounded-md">Créer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search modal */}
      {searchOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSearchOpen(false)}>
          <div className="bg-white rounded-lg max-w-3xl w-full p-6 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="uke-search-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-display text-[#0B2545]">Recherche UKE</h3>
              <button onClick={() => setSearchOpen(false)} className="text-[#5B5B62] hover:text-[#1F2937]"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex gap-2 mb-4">
              <input
                data-testid="uke-search-input"
                autoFocus
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
                placeholder="ex: cybersécurité, immigration, entrepreneuriat…"
                className="flex-1 border border-[#E4E1D8] rounded-md px-3 py-2 text-sm"
              />
              <button data-testid="uke-search-submit" onClick={runSearch} className="bg-[#0B2545] hover:bg-[#123163] text-white px-4 py-2 text-sm rounded-md">Chercher</button>
            </div>
            <div className="overflow-y-auto space-y-2 flex-1">
              {searchResults.map((r) => (
                <div key={r.id} className="border border-[#E4E1D8] rounded-md p-3" data-testid={`uke-search-result-${r.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#1F2937]">{r.title}</span>
                    <Badge tone="neutral">{r.kind}</Badge>
                    <Badge tone="gold">{(r.quality || 0).toFixed(0)}</Badge>
                  </div>
                  {r.summary_fr && <div className="mt-1 text-xs text-[#5B5B62]">{r.summary_fr.slice(0, 200)}</div>}
                  {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="mt-1 text-[11px] text-[#0B2545] hover:underline">{r.url}</a>}
                </div>
              ))}
              {searchResults.length === 0 && searchQ && <div className="text-sm text-[#5B5B62] text-center py-6">Aucun résultat.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone = "neutral", testId }) {
  const toneClass = tone === "gold" ? "border-[#E7D9B3] bg-[#FBF6EB]" : tone === "warn" ? "border-amber-200 bg-amber-50" : "border-[#E4E1D8] bg-white";
  return (
    <div className={`border ${toneClass} rounded-md p-4`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#5B5B62]">{label}</div>
        <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
      </div>
      <div className="mt-2 text-2xl font-display text-[#0B2545]">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#5B5B62] mb-1">{label}</div>
      {children}
    </label>
  );
}
